import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react"
import { ChevronDown, X } from "lucide-react"

import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import type { CallToAction } from "@/api/hooks/campaignForm"
import type { SelectedCta } from "../state/formReducer"

/**
 * Call-to-action picker — TikTok Ads Manager style:
 * tag chips in a closed field, dropdown with search + checkboxes.
 */

const PREFERRED_CTAS = [
  "view now",
  "learn more",
  "read more",
  "check it out",
  "watch now",
  "interested",
]
const PREFERRED_SET = new Set(PREFERRED_CTAS)

interface Props {
  ctas: CallToAction[] | undefined
  selected: SelectedCta[]
  onToggle: (cta: SelectedCta) => void
}

function toSelected(cta: CallToAction): SelectedCta {
  const assetIds = cta.id?.asset_ids ?? []
  const value = assetIds.length > 0 ? String(assetIds[0]) : ""
  return {
    id: cta.id as Record<string, unknown>,
    name: cta.name,
    value,
  }
}

function preferredRank(name: string): number {
  const idx = PREFERRED_CTAS.indexOf(name.trim().toLowerCase())
  return idx === -1 ? PREFERRED_CTAS.length : idx
}

export function CtaPicker({ ctas, selected, onToggle }: Props) {
  const list = useMemo(() => ctas ?? [], [ctas])
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    function handler(e: globalThis.MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  // Clear the query whenever the dropdown is closed (the search input
  // only exists while open, so this is a one-shot reset per close).
  if (!open && search !== "") setSearch("")

  useEffect(() => {
    if (open) searchRef.current?.focus()
  }, [open])

  const selectedNames = useMemo(
    () => new Set(selected.map((s) => s.name)),
    [selected],
  )

  const { preferred, others } = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matches = (c: CallToAction) =>
      !q || c.name.toLowerCase().includes(q)

    const preferredList = list
      .filter(
        (c) => PREFERRED_SET.has(c.name.trim().toLowerCase()) && matches(c),
      )
      .sort((a, b) => preferredRank(a.name) - preferredRank(b.name))

    const otherList = list
      .filter(
        (c) => !PREFERRED_SET.has(c.name.trim().toLowerCase()) && matches(c),
      )
      .sort((a, b) => a.name.localeCompare(b.name))

    return { preferred: preferredList, others: otherList }
  }, [list, search])

  const hasResults = preferred.length > 0 || others.length > 0

  function clearAll(e: MouseEvent) {
    e.stopPropagation()
    ;[...selected].forEach((cta) => onToggle(cta))
  }

  function onTriggerKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      setOpen((v) => !v)
    } else if (e.key === "Escape") {
      setOpen(false)
    }
  }

  function renderCtaRow(cta: CallToAction) {
    const checked = selectedNames.has(cta.name)
    return (
      <label
        key={cta.name}
        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-white hover:bg-[#3a3a3a]"
      >
        <Checkbox
          checked={checked}
          onCheckedChange={() => onToggle(toSelected(cta))}
        />
        <span>{cta.name}</span>
      </label>
    )
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div
        role="combobox"
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen(true)}
        onKeyDown={onTriggerKeyDown}
        className="flex min-h-10 w-full cursor-text items-center gap-2 rounded-md border border-[#5a5a5a] bg-[#1f1f1f] px-2.5 py-1.5 text-left hover:border-[#7a7a7a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {selected.length === 0 ? (
            <span className="text-sm text-white/50">Search call to action...</span>
          ) : (
            selected.map((cta) => (
              <span
                key={cta.name}
                className="inline-flex max-w-full items-center gap-1 rounded border border-[#5a5a5a] bg-[#3a3a3a] px-2 py-0.5 text-xs text-white"
              >
                <span className="truncate">{cta.name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${cta.name}`}
                  className="shrink-0 text-white/70 hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggle(cta)
                  }}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          )}
        </div>
        {selected.length > 0 ? (
          <button
            type="button"
            aria-label="Clear all CTAs"
            className="shrink-0 rounded-full p-0.5 text-white/50 hover:bg-[#3a3a3a] hover:text-white"
            onClick={clearAll}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
        <button
          type="button"
          aria-label={open ? "Close CTA list" : "Open CTA list"}
          className="shrink-0 text-white/50 hover:text-white"
          onClick={(e) => {
            e.stopPropagation()
            setOpen((v) => !v)
          }}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open ? (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-[#5a5a5a] bg-[#262626] shadow-lg">
          <div className="border-b border-[#4a4a4a] p-2">
            <Input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="h-8"
            />
          </div>
          <div className="max-h-56 overflow-y-auto p-1" role="listbox">
            {!hasResults ? (
              <p className="px-2 py-3 text-center text-xs text-white/50">
                {list.length === 0 ? "No CTAs available" : "No matches found"}
              </p>
            ) : (
              <>
                {preferred.length > 0 ? (
                  <div>
                    <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/45">
                      Suggested
                    </p>
                    {preferred.map(renderCtaRow)}
                  </div>
                ) : null}
                {preferred.length > 0 && others.length > 0 ? (
                  <div className="my-1 border-t border-[#4a4a4a]" />
                ) : null}
                {others.length > 0 ? (
                  <div>
                    <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/45">
                      All
                    </p>
                    {others.map(renderCtaRow)}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
